import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './query-keys';
import type {
  Article,
  ArticleFilters,
  PublicArticleFilters,
  NewArticle,
  UpdateArticle,
  AudioRecording,
  NewAudioRecording,
  UpdateAudioRecording,
  GetAudioUploadUrlRequest,
} from '@/types/article';

// ============================================================================
// Query Options
// ============================================================================

// Query options for a single article
export const articleQueryOptions = (id: string) =>
  queryOptions({
    queryKey: queryKeys.articles.detail(id),
    queryFn: async () => {
      const response = await api.getArticle(id);
      return response.article;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

// Query options for topics
export const topicsQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.articles.topics(),
    queryFn: async () => {
      const response = await api.getTopics();
      return response.topics;
    },
    staleTime: 1000 * 60 * 60, // 1 hour (topics rarely change)
  });

// ============================================================================
// Query Hooks
// ============================================================================

// Hook to fetch user's articles with optional filters
export function useArticles(filters?: ArticleFilters) {
  return useQuery({
    queryKey: queryKeys.articles.list(filters || {}),
    queryFn: async () => {
      const response = await api.getArticles(filters);
      return response.articles;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to fetch a single article
export function useArticle(id: string) {
  return useQuery(articleQueryOptions(id));
}

// Hook to fetch public articles for browsing
export function usePublicArticles(filters?: PublicArticleFilters) {
  return useQuery({
    queryKey: queryKeys.articles.publicList(filters || {}),
    queryFn: async () => {
      const response = await api.getPublicArticles(filters);
      return response.articles;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to fetch available topics
export function useTopics() {
  return useQuery(topicsQueryOptions());
}

// Hook to fetch comments for an article
export function useArticleComments(articleId: string) {
  return useQuery({
    queryKey: queryKeys.articles.comments(articleId),
    queryFn: async () => {
      const response = await api.getArticleComments(articleId);
      return response.comments;
    },
    staleTime: 1000 * 60, // 1 minute
  });
}

// Hook to fetch shares for an article
export function useArticleShares(articleId: string) {
  return useQuery({
    queryKey: queryKeys.articles.shares(articleId),
    queryFn: async () => {
      const response = await api.getArticleShares(articleId);
      return response.shares;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to fetch article analytics
export function useArticleAnalytics() {
  return useQuery({
    queryKey: queryKeys.articles.analytics(),
    queryFn: async () => {
      const response = await api.getArticleAnalytics();
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

// Hook to create a new article
export function useCreateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (article: NewArticle) => api.createArticle(article),
    onSuccess: () => {
      // Invalidate article list to refetch
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.lists(),
      });
      // Navigation handled by the caller
    },
  });
}

// Hook to update an article
export function useUpdateArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateArticle }) =>
      api.updateArticle(id, data),
    // Optimistic updates for instant UI feedback
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.articles.detail(id),
      });

      // Snapshot previous value
      const previous = queryClient.getQueryData<Article>(
        queryKeys.articles.detail(id)
      );

      // Optimistically update
      if (previous) {
        queryClient.setQueryData<Article>(
          queryKeys.articles.detail(id),
          { ...previous, ...data }
        );
      }

      return { previous, id };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.articles.detail(context.id),
          context.previous
        );
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate to ensure we have latest data
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.lists(),
      });
    },
  });
}

// Hook to delete an article
export function useDeleteArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteArticle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.lists(),
      });
      // Navigation handled by the caller
    },
  });
}

// Hook to share an article
export function useShareArticle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, email }: { articleId: string; email: string }) =>
      api.shareArticle(articleId, email),
    onSuccess: (_, { articleId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.shares(articleId),
      });
    },
  });
}

// Hook to remove share access
export function useRemoveArticleShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, userId }: { articleId: string; userId: string }) =>
      api.removeArticleShare(articleId, userId),
    onSuccess: (_, { articleId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.shares(articleId),
      });
    },
  });
}

// Hook to record a view
export function useRecordArticleView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (articleId: string) => api.recordArticleView(articleId),
    onSuccess: (response, articleId) => {
      // Update the view count in the cached article
      const previous = queryClient.getQueryData<Article>(
        queryKeys.articles.detail(articleId)
      );
      if (previous) {
        queryClient.setQueryData<Article>(
          queryKeys.articles.detail(articleId),
          { ...previous, view_count: response.viewCount }
        );
      }
    },
  });
}

// Hook to add a comment
export function useAddArticleComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, content }: { articleId: string; content: string }) =>
      api.addArticleComment(articleId, content),
    onSuccess: (_, { articleId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.comments(articleId),
      });
      // Also update the comment count in the article
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.detail(articleId),
      });
    },
  });
}

// Hook to update a comment
export function useUpdateArticleComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      articleId,
      commentId,
      content,
    }: {
      articleId: string;
      commentId: string;
      content: string;
    }) => api.updateArticleComment(articleId, commentId, content),
    onSuccess: (_, { articleId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.comments(articleId),
      });
    },
  });
}

// Hook to delete a comment
export function useDeleteArticleComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, commentId }: { articleId: string; commentId: string }) =>
      api.deleteArticleComment(articleId, commentId),
    onSuccess: (_, { articleId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.comments(articleId),
      });
      // Also update the comment count in the article
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.detail(articleId),
      });
    },
  });
}

// ============================================================================
// Audio Recording Hooks
// ============================================================================

// Hook to fetch audio recordings for an article
export function useArticleRecordings(articleId: string) {
  return useQuery({
    queryKey: queryKeys.articles.recordings(articleId),
    queryFn: async () => {
      const response = await api.getAudioRecordings(articleId);
      return response.recordings;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook to get a presigned upload URL
export function useGetAudioUploadUrl() {
  return useMutation({
    mutationFn: ({ articleId, data }: { articleId: string; data: GetAudioUploadUrlRequest }) =>
      api.getAudioUploadUrl(articleId, data),
  });
}

// Hook to create an audio recording
export function useCreateAudioRecording() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, data }: { articleId: string; data: NewAudioRecording }) =>
      api.createAudioRecording(articleId, data),
    onSuccess: (_, { articleId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.recordings(articleId),
      });
      // Also update the recording count in the article
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.detail(articleId),
      });
    },
  });
}

// Hook to get a presigned playback URL
export function useGetAudioPlaybackUrl() {
  return useMutation({
    mutationFn: ({ articleId, recordingId }: { articleId: string; recordingId: string }) =>
      api.getAudioPlaybackUrl(articleId, recordingId),
  });
}

// Hook to update an audio recording
export function useUpdateAudioRecording() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      articleId,
      recordingId,
      data,
    }: {
      articleId: string;
      recordingId: string;
      data: UpdateAudioRecording;
    }) => api.updateAudioRecording(articleId, recordingId, data),
    onMutate: async ({ articleId, recordingId, data }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.articles.recordings(articleId),
      });

      const previous = queryClient.getQueryData<AudioRecording[]>(
        queryKeys.articles.recordings(articleId)
      );

      if (previous) {
        queryClient.setQueryData<AudioRecording[]>(
          queryKeys.articles.recordings(articleId),
          previous.map((r) =>
            r.id === recordingId ? { ...r, ...data } : r
          )
        );
      }

      return { previous, articleId };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          queryKeys.articles.recordings(context.articleId),
          context.previous
        );
      }
    },
    onSuccess: (_, { articleId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.recordings(articleId),
      });
    },
  });
}

// Hook to delete an audio recording
export function useDeleteAudioRecording() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, recordingId }: { articleId: string; recordingId: string }) =>
      api.deleteAudioRecording(articleId, recordingId),
    onSuccess: (_, { articleId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.recordings(articleId),
      });
      // Also update the recording count in the article
      queryClient.invalidateQueries({
        queryKey: queryKeys.articles.detail(articleId),
      });
    },
  });
}
